import { Order } from '@seen/core/models/orders/Order'
import type { QueryTypes } from '@seen/core/search'
import { useContext, useMemo } from 'react'
import { CurrentParticipantContext } from '#src/contexts/current-participant/CurrentParticipantContext.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

interface UseOrderSearchByIdentifierResult {
  data: Order[] | undefined
  isLoading: boolean
}

// Look up a ServiceRequest by its seen identifier (e.g. `SR-6976`) on demand.
// No-op for any input that doesn't parse as an SR identifier. Scopes by
// `CurrentParticipantContext` when set.
//
// `matchBareNumber` opts into treating a bare all-digit input (e.g. `372`) as
// `SR-372`. It defaults to false so global callers like the command menu and
// mention picker — which pass arbitrary input — don't fire an order lookup on
// every numeric keystroke. The orders page is order-scoped, so there a bare
// number unambiguously refers to an order id and can opt in.
export function useOrderSearchByIdentifier(
  input: string,
  options?: { matchBareNumber?: boolean }
): UseOrderSearchByIdentifierResult {
  const participant = useContext(CurrentParticipantContext)
  const matchBareNumber = options?.matchBareNumber ?? false

  const query = useMemo<QueryTypes | undefined>(() => {
    let normalized = Order.parseSeenIdentifier(input)
    if (!normalized && matchBareNumber) {
      const trimmed = input.trim()
      if (/^\d+$/.test(trimmed)) {
        normalized = `SR-${trimmed}`
      }
    }
    if (!normalized) {
      return undefined
    }
    const base = Order.bySeenIdentifier(normalized)
    return participant ? { ...base, patient: participant.reference } : base
  }, [input, participant, matchBareNumber])

  const { data, isLoading } = useSearch(Order, query)
  return { data, isLoading }
}
